/**
 * cron 표현식 판독. 문법 검증과 한국어 풀이를 한다.
 *
 * Owns:
 * - 상류(croniter) 5자리 문법의 부분집합 파싱과 자리별 오류 문구.
 * - 상류 `MIN_INTERVAL_SEC` 을 미러한 최소 간격(5분) 판정.
 * - 표현식 → 사용자 문장(`매일 02:00`) 풀이와 그 어휘.
 *
 * Does not own:
 * - 최종 판정. 상류가 422 로 거절할 수 있다. 화면 문구는 그 경우를 전제한다.
 * - 스케줄 초안의 다른 축(간격·기간·발화 게이트). 페이지의 `scheduleDraft` 가 가진다.
 * - 타임존 해석. cron 은 문자열만 본다. 어느 벽시계 기준으로 실행하는지는 초안이 정한다.
 */

const pad2 = (value: number): string => String(value).padStart(2, "0");

// ---- cron 표현식 검증 -------------------------------------------------------------------
//
// 상류(croniter)가 받는 5자리 문법의 부분집합을 같은 규칙으로 읽는다. 각 자리가 받는 형식:
//   - `*`, 값, 범위 a-b, 목록 a,b
//   - 간격 */n·a-b/n·a/n
//   - 월·요일 이름(jan/mon), 매크로(@daily 등)
// 상류만 아는 확장(L·#·W)은 여기서 거부한다. 최종 판정은 상류가 한다
// (422 → 400 upstream.invalid_request). 그래서 문구에 "저장할 때 서버가 확인합니다"를 유지한다.

export type CronValidation = { kind: "valid" } | { kind: "invalid"; message: string };

// 상류 MIN_INTERVAL_SEC 미러. 5분보다 자주 실행하는 식은 상류가 거절한다.
const CRON_MIN_GAP_MINUTES = 5;

// 매크로와 그 풀이. 검증(CRON_MACROS)과 풀이(describeCronExpression)가 같은 목록을 읽는다.
const CRON_MACRO_TEXT: Readonly<Record<string, string>> = {
  "@hourly": "매시 00분",
  "@daily": "매일 00:00",
  "@midnight": "매일 00:00",
  "@weekly": "매주 일요일 00:00",
  "@monthly": "매월 1일 00:00",
  "@yearly": "매년 1월 1일 00:00",
  "@annually": "매년 1월 1일 00:00",
};
const CRON_MACROS: ReadonlySet<string> = new Set(Object.keys(CRON_MACRO_TEXT));

type CronFieldSpec = {
  label: string;
  min: number;
  max: number;
  names?: Readonly<Record<string, number>>;
};

const MONTH_NAMES: Readonly<Record<string, number>> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const DAY_NAMES: Readonly<Record<string, number>> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const CRON_FIELDS: readonly CronFieldSpec[] = [
  { label: "분", min: 0, max: 59 },
  { label: "시", min: 0, max: 23 },
  { label: "일", min: 1, max: 31 },
  { label: "월", min: 1, max: 12, names: MONTH_NAMES },
  // 7 은 일요일의 다른 표기다(crontab 관례). 상류도 받는다.
  { label: "요일", min: 0, max: 7, names: DAY_NAMES },
];

function cronFieldMessage(spec: CronFieldSpec): string {
  return `cron 표현식의 ${spec.label} 자리가 잘못됐습니다. ${spec.min}~${spec.max} 사이 값이나 *, 범위(a-b), 목록(a,b), 간격(*/n)을 쓸 수 있습니다.`;
}

// 한 자리의 값 하나를 숫자로 바꾼다. 이름은 그 자리가 이름을 받을 때만 해석한다.
function parseCronValue(token: string, spec: CronFieldSpec): number | null {
  if (/^\d+$/.test(token)) {
    const value = Number(token);
    return value >= spec.min && value <= spec.max ? value : null;
  }
  const named = spec.names?.[token.toLowerCase()];
  return named === undefined ? null : named;
}

// 한 자리(예: "0,30" · "*/6" · "1-5") 가 뜻하는 값의 집합. 문법이 틀리면 null.
function expandCronField(field: string, spec: CronFieldSpec): Set<number> | null {
  const values = new Set<number>();
  for (const item of field.split(",")) {
    if (item === "") {
      return null;
    }
    const [rangePart, stepPart, ...rest] = item.split("/");
    if (rest.length > 0 || rangePart === undefined) {
      return null;
    }
    let step = 1;
    if (stepPart !== undefined) {
      if (!/^\d+$/.test(stepPart) || Number(stepPart) < 1) {
        return null;
      }
      step = Number(stepPart);
    }
    let start: number;
    let end: number;
    if (rangePart === "*" || rangePart === "?") {
      start = spec.min;
      end = spec.max;
    } else if (rangePart.includes("-")) {
      const [from, to, ...more] = rangePart.split("-");
      if (more.length > 0 || from === undefined || to === undefined) {
        return null;
      }
      const fromValue = parseCronValue(from, spec);
      const toValue = parseCronValue(to, spec);
      if (fromValue === null || toValue === null || fromValue > toValue) {
        return null;
      }
      start = fromValue;
      end = toValue;
    } else {
      const single = parseCronValue(rangePart, spec);
      if (single === null) {
        return null;
      }
      start = single;
      // "a/n" 은 a 부터 자리 끝까지 n 간격이다(croniter 관례). 간격이 없으면 그 값 하나다.
      end = stepPart === undefined ? single : spec.max;
    }
    for (let value = start; value <= end; value += step) {
      values.add(value);
    }
  }
  return values;
}

/**
 * cron 표현식의 문법과 최소 간격을 판정한다. 빈 값은 여기서 invalid 가 아니다. "입력해
 * 주세요" 는 호출부가 처리한다. 그래서 valid 로 둔다. 호출부가 빈 값인지 먼저 확인한다.
 */
export function validateCronExpression(expression: string): CronValidation {
  const trimmed = expression.trim();
  if (trimmed === "") {
    return { kind: "valid" };
  }
  if (trimmed.startsWith("@")) {
    return CRON_MACROS.has(trimmed.toLowerCase())
      ? { kind: "valid" }
      : {
          kind: "invalid",
          message: "cron 매크로는 @hourly, @daily, @weekly, @monthly, @yearly 만 쓸 수 있습니다.",
        };
  }
  const fields = trimmed.split(/\s+/);
  if (fields.length !== CRON_FIELDS.length) {
    return {
      kind: "invalid",
      message: "cron 표현식은 분·시·일·월·요일 다섯 자리여야 합니다.",
    };
  }
  const expanded: Set<number>[] = [];
  for (const [index, spec] of CRON_FIELDS.entries()) {
    const values = expandCronField(fields[index] ?? "", spec);
    if (values === null) {
      return { kind: "invalid", message: cronFieldMessage(spec) };
    }
    expanded.push(values);
  }
  // 최소 간격. 두 간격을 확인한다.
  //   - 같은 시 안의 분 값 사이 간격.
  //   - 시 값이 이어질 때 시 경계를 넘는 간격.
  // 상류는 1년치 실행 시각을 모두 계산해 판정한다. 이 검사는 그 앞 단계다. 대부분의 위반은
  // 분 자리에서 생긴다.
  const minutes = [...(expanded[0] ?? [])].sort((a, b) => a - b);
  const hours = [...(expanded[1] ?? [])].sort((a, b) => a - b);
  const tooFrequent = {
    kind: "invalid" as const,
    message: `cron 표현식이 ${CRON_MIN_GAP_MINUTES}분보다 자주 실행됩니다. 실행 간격은 ${CRON_MIN_GAP_MINUTES}분 이상이어야 합니다.`,
  };
  for (let index = 1; index < minutes.length; index += 1) {
    if ((minutes[index] ?? 0) - (minutes[index - 1] ?? 0) < CRON_MIN_GAP_MINUTES) {
      return tooFrequent;
    }
  }
  if (
    minutes.length > 1 &&
    60 - (minutes[minutes.length - 1] ?? 0) + (minutes[0] ?? 0) < CRON_MIN_GAP_MINUTES
  ) {
    const hasAdjacentHours = hours.some(
      (hour, index) => index > 0 && hour - (hours[index - 1] ?? 0) === 1,
    );
    if (
      hasAdjacentHours ||
      (hours.includes(0) && hours.includes(23) && hasConsecutiveCronDays(fields, expanded))
    ) {
      return tooFrequent;
    }
  }
  return { kind: "valid" };
}

function hasConsecutiveCronDays(fields: string[], expanded: Set<number>[]): boolean {
  const days = expanded[2] ?? new Set<number>();
  const months = expanded[3] ?? new Set<number>();
  const weekdays = new Set([...(expanded[4] ?? [])].map((day) => day % 7));
  const dayField = fields[2] ?? "";
  const weekdayField = fields[4] ?? "";
  // croniter는 전체 범위도 반대 필드에 *가 없으면 명시적인 조건으로 유지한다.
  const anyDay =
    dayField === "*" || dayField === "?" || (days.size === 31 && weekdayField.includes("*"));
  const anyWeekday =
    weekdayField === "*" || weekdayField === "?" || (weekdays.size === 7 && dayField.includes("*"));
  const matches = (date: Date): boolean => {
    if (!months.has(date.getUTCMonth() + 1)) return false;
    const dayMatches = days.has(date.getUTCDate());
    const weekdayMatches = weekdays.has(date.getUTCDay());
    if (anyDay) return anyWeekday || weekdayMatches;
    if (anyWeekday) return dayMatches;
    return dayMatches || weekdayMatches;
  };
  // 상류의 UTC 기준일·366일 검사 구간과 맞추고, 마지막 날의 다음 날도 비교한다.
  const date = new Date(Date.UTC(2025, 0, 1));
  let previousMatches = matches(date);
  for (let index = 0; index < 366; index += 1) {
    date.setUTCDate(date.getUTCDate() + 1);
    const currentMatches = matches(date);
    if (previousMatches && currentMatches) return true;
    previousMatches = currentMatches;
  }
  return false;
}

// ---- cron 표현식 풀이 -----------------------------------------------------------------

const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

// 정렬된 값이 0 부터 같은 간격으로 자리 끝까지 이어지는지(`*/n`) 판정한다. 그 간격을 반환한다.
function uniformStep(values: number[], size: number): number | null {
  if (values.length < 2 || values[0] !== 0) {
    return null;
  }
  const step = (values[1] ?? 0) - (values[0] ?? 0);
  if (step <= 0 || size % step !== 0 || values.length !== size / step) {
    return null;
  }
  return values.every((value, index) => value === index * step) ? step : null;
}

function isFull(values: number[], spec: CronFieldSpec): boolean {
  return values.length >= spec.max - spec.min + 1;
}

const joinList = (items: string[]): string => items.join(", ");

// 요일 집합 → "월·수·금" / "평일" / "주말" / "매일". 7(일요일 별칭)은 0 으로 바꾼다.
function describeDays(values: number[]): string | null {
  const days = [...new Set(values.map((value) => value % 7))].sort((a, b) => a - b);
  if (days.length === 7) {
    return null;
  }
  const key = days.join(",");
  if (key === "1,2,3,4,5") {
    return "평일";
  }
  if (key === "0,6") {
    return "주말";
  }
  return `${days.map((day) => DAY_LABELS[day] ?? String(day)).join("·")}요일`;
}

/**
 * cron 표현식을 한국어 한 줄로 푼다(예: "매일 02:00", "평일 6시간마다", "매월 1일 09:00").
 * 문법이 틀리면 null 을 반환한다. 흔한 모양을 우선하고, 조합이 많은 식은 "하루 N회"처럼
 * 요약한다. 원래 식은 입력 칸에 그대로 있다. 그래서 풀이는 확인용이다.
 */
export function describeCronExpression(expression: string): string | null {
  const trimmed = expression.trim();
  if (trimmed === "" || validateCronExpression(trimmed).kind === "invalid") {
    return null;
  }
  const macro = trimmed.toLowerCase();
  if (macro.startsWith("@")) {
    return CRON_MACRO_TEXT[macro] ?? null;
  }
  const fields = trimmed.split(/\s+/);
  const sets = CRON_FIELDS.map((spec, index) => expandCronField(fields[index] ?? "", spec));
  if (sets.some((set) => set === null)) {
    return null;
  }
  const [minutes, hours, doms, months, dows] = sets.map((set) =>
    [...(set ?? [])].sort((a, b) => a - b),
  );
  const [minuteSpec, hourSpec, domSpec, monthSpec, dowSpec] = CRON_FIELDS;
  if (
    minutes === undefined ||
    hours === undefined ||
    doms === undefined ||
    months === undefined ||
    dows === undefined ||
    minuteSpec === undefined ||
    hourSpec === undefined ||
    domSpec === undefined ||
    monthSpec === undefined ||
    dowSpec === undefined
  ) {
    return null;
  }

  // 날짜 축: 일·월·요일. cron 에서 일과 요일이 둘 다 제한되면 두 조건은 OR 다.
  const dayNames = isFull(dows, dowSpec) ? null : describeDays(dows);
  const domText = isFull(doms, domSpec) ? null : `${joinList(doms.map(String))}일`;
  const monthText = isFull(months, monthSpec) ? null : `${joinList(months.map(String))}월`;
  let dayPart: string;
  if (domText === null && dayNames === null) {
    dayPart = monthText === null ? "매일" : `${monthText} 매일`;
  } else if (domText !== null && dayNames !== null) {
    dayPart = `${monthText ?? "매월"} ${domText} 또는 ${dayNames}`;
  } else if (domText !== null) {
    dayPart = monthText === null ? `매월 ${domText}` : `매년 ${monthText} ${domText}`;
  } else {
    // "평일"·"주말"은 이미 주 단위 표현이다. 그래서 "매주"를 붙이지 않는다.
    const names = dayNames ?? "";
    const weekly = names.endsWith("요일") ? `매주 ${names}` : names;
    dayPart = monthText === null ? weekly : `${monthText} ${names}`;
  }

  // 시각 축. 간격형("6시간마다")과 시각 나열형("02:00, 14:00")을 구분한다.
  const minuteStep = uniformStep(minutes, 60);
  const hourStep = uniformStep(hours, 24);
  let timePart: string;
  if (isFull(hours, hourSpec) && isFull(minutes, minuteSpec)) {
    timePart = "매분";
  } else if (isFull(hours, hourSpec) && minuteStep !== null) {
    timePart = `${minuteStep}분마다`;
  } else if (isFull(hours, hourSpec)) {
    timePart = `매시 ${joinList(minutes.map((minute) => `${pad2(minute)}분`))}`;
  } else if (hourStep !== null && minutes.length === 1) {
    const minute = minutes[0] ?? 0;
    timePart = `${hourStep}시간마다${minute === 0 ? "" : ` (${pad2(minute)}분)`}`;
  } else if (hours.length * minutes.length <= 6) {
    timePart = joinList(
      hours.flatMap((hour) => minutes.map((minute) => `${pad2(hour)}:${pad2(minute)}`)),
    );
  } else {
    timePart = `하루 ${hours.length * minutes.length}회`;
  }
  // "매일 6시간마다"는 불필요하게 길다. 날짜 축이 기본(매일)이면 간격형 시각만 표시한다.
  const intervalLike = /(분마다|시간마다|^매시|^매분)/.test(timePart);
  return dayPart === "매일" && intervalLike ? timePart : `${dayPart} ${timePart}`;
}
